/**
 * 2026-02-18 Bug #8 — Customer site edit, full-page.
 *
 * Replaces the SitesBlock "edit site" modal that violated the no-modal
 * contract (map was rendering behind the modal layer and unusable).
 *
 * Layout (mobile-first, 360 px floor):
 *   • Header: back chevron, site name, save / cancel
 *   • Body  (>= md):    [Editor | Map] side-by-side
 *           (< md):     editor stacked above the map
 *
 * Map = the existing `PlaceInput` (Leaflet-based, drop-a-pin, geocode
 * search). Selecting a result pushes lat/lng/address back into the
 * editor state. Mobile users get the same one-tap experience.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import PlaceInput from "../components/PlaceInput";
import BlurTextarea from "../components/BlurTextarea";

export default function CustomerSiteEditPage() {
  const { cid, sid } = useParams();
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const [siteR, custR] = await Promise.all([
          api.get(`/sites/${sid}`),
          api.get(`/customers/${cid}`),
        ]);
        if (cancelled) return;
        setSite(siteR.data);
        setCustomerName(custR.data?.name || "");
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load site");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [cid, sid]);

  const place = useMemo(() => (
    site?.address
      ? {
          address: site.address,
          lat: site.lat,
          lng: site.lng,
          place_id: site.place_id,
          source: site.address_source || "legacy",
        }
      : null
  ), [site]);

  const onPlace = (v) => {
    setSite((s) => ({
      ...s,
      address: v?.address || "",
      lat: v?.lat ?? null,
      lng: v?.lng ?? null,
      place_id: v?.place_id ?? null,
      address_source: v?.source ?? null,
    }));
  };

  const save = async () => {
    if (!site) return;
    if (!(site.name || "").trim()) {
      toast.error("Site name is required.");
      return;
    }
    if (!(site.address || "").trim()) {
      toast.error("Address is required.");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/sites/${sid}`, {
        ...site,
        lat: site.lat ? Number(site.lat) : null,
        lng: site.lng ? Number(site.lng) : null,
      });
      toast.success("Site saved.");
      navigate(`/customers/${cid}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-sm text-[#647087]" data-testid="site-edit-loading">
        <Loader2 className="w-4 h-4 inline animate-spin" /> Loading site…
      </div>
    );
  }
  if (err) {
    return (
      <div className="p-6 text-sm text-rose-300" data-testid="site-edit-error">
        {err}
        <div className="mt-2">
          <Link to={`/customers/${cid}`} className="text-[#1FB6C1] hover:underline">← back to customer</Link>
        </div>
      </div>
    );
  }
  if (!site) return null;

  return (
    <div className="p-3 md:p-6 max-w-[1600px] mx-auto" data-testid="site-edit-page">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Link
          to={`/customers/${cid}`}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="site-edit-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to {customerName || "customer"}
        </Link>
        <div className="text-xs uppercase tracking-wider text-[#647087] ml-2">
          Editing site
        </div>
        <h1 className="text-lg font-bold text-white truncate min-w-0">
          {site.name || "Untitled site"}
        </h1>
        <div className="ml-auto flex gap-1.5">
          <button
            type="button"
            onClick={() => navigate(`/customers/${cid}`)}
            className="hv-btn hv-btn-ghost text-xs"
            data-testid="site-edit-cancel"
          >
            <XCircle className="w-3.5 h-3.5" /> Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={busy}
            className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
            data-testid="site-edit-save"
          >
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {/* Editor card. The map opens as a full-viewport pin picker
          on demand via PlaceInput (clicking the "Drop pin" affordance).
          Because this whole page is a route — NOT a modal — the
          pin-picker overlay renders at the top of the document and is
          fully interactive, fixing the original "map behind modal"
          contract violation. */}
      <div className="hv-card p-3 space-y-3 max-w-3xl" data-testid="site-edit-form">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
            Site name <span className="text-rose-400">*</span>
          </label>
          <input
            value={site.name ?? ""}
            onChange={(e) => setSite((s) => ({ ...s, name: e.target.value }))}
            className="hv-input mt-1 w-full"
            data-testid="site-edit-input-name"
            placeholder="e.g. Main building, Office block, Plant room"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
            Address <span className="text-rose-400">*</span>
          </label>
          <PlaceInput
            value={place}
            onChange={onPlace}
            testId="site-edit-input-address"
            required
          />
          {site.lat != null && site.lng != null && (
            <div className="text-[10px] text-[#647087] font-mono mt-1">
              {Number(site.lat).toFixed(6)}, {Number(site.lng).toFixed(6)}
            </div>
          )}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
            Notes
          </label>
          <BlurTextarea
            value={site.notes || ""}
            onSave={(v) => setSite((s) => ({ ...s, notes: v }))}
            testid="site-edit-input-notes"
            placeholder="Access info, gate codes, contact on site, etc."
          />
        </div>
      </div>
    </div>
  );
}
